// pdf.js, erst bei Bedarf geladen: Seiten anzeigen (components/Pdf.tsx) und Text lesen (pdfText, pdfPages).

type PdfJs = typeof import('pdfjs-dist/legacy/build/pdf.mjs');
let pdfjs: Promise<PdfJs> | null = null;
export function loadPdfJs(): Promise<PdfJs> {
  // „legacy“: läuft auch in älteren Handy-Browsern
  pdfjs ??= Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
  ]).then(
    ([lib, worker]) => {
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    },
    (err: unknown) => {
      pdfjs = null; // z. B. kurz offline: beim nächsten Öffnen neu versuchen
      throw err;
    },
  );
  return pdfjs;
}

// Schriften, Zeichentabellen und Decoder für gescannte PDFs (JBIG2, JPEG 2000) liefert der Build unter /pdfjs/ mit
// (vite.config.ts), nichts kommt von fremden Servern.
export const PDF_ASSETS = {
  cMapUrl: '/pdfjs/cmaps/',
  cMapPacked: true,
  standardFontDataUrl: '/pdfjs/standard_fonts/',
  wasmUrl: '/pdfjs/wasm/',
  iccUrl: '/pdfjs/iccs/',
};

// Text der ersten Seiten einer PDF, z. B. um Lohnunterlagen zu erkennen. Wirft bei kaputten oder geschützten PDFs.
export async function pdfText(data: ArrayBuffer, pages = 2): Promise<string> {
  const lib = await loadPdfJs();
  const task = lib.getDocument({ data: new Uint8Array(data), ...PDF_ASSETS });
  try {
    const pdf = await task.promise;
    let text = '';
    for (let i = 1; i <= Math.min(pages, pdf.numPages); i++) {
      const c = await (await pdf.getPage(i)).getTextContent();
      text += c.items.map((it) => ('str' in it ? it.str : '')).join(' ') + '\n';
    }
    return text;
  } finally {
    void task.destroy();
  }
}

interface TextPiece {
  str: string;
  transform: number[];
  width: number;
  height: number;
}

// Text einer Seite mit Zeilen wie auf dem Papier: Stücke auf gleicher Höhe sind eine Zeile, größere Abstände (Spalten)
// werden ein Tabulator. So bleibt bei Formularen (z. B. einem Personalstammblatt) erkennbar, welcher Wert zu welcher
// Angabe gehört.
function pageText(items: unknown[]): string {
  const pieces = items.filter(
    (it): it is TextPiece => !!it && typeof it === 'object' && 'str' in it && 'transform' in it && !!(it as TextPiece).str.trim(),
  );
  const size = (p: TextPiece) => p.height || Math.hypot(p.transform[2]!, p.transform[3]!) || 8;
  pieces.sort((a, b) => b.transform[5]! - a.transform[5]! || a.transform[4]! - b.transform[4]!);
  const lines: TextPiece[][] = [];
  let y = 0;
  for (const p of pieces) {
    const py = p.transform[5]!;
    if (lines.length && Math.abs(py - y) <= Math.max(2, size(p) / 2)) lines[lines.length - 1]!.push(p);
    else {
      lines.push([p]);
      y = py;
    }
  }
  return lines
    .map((line) => {
      line.sort((a, b) => a.transform[4]! - b.transform[4]!);
      let out = '';
      let end = 0;
      for (const p of line) {
        const gap = p.transform[4]! - end;
        if (out) out += gap > size(p) * 1.5 ? '\t' : gap > size(p) * 0.15 ? ' ' : '';
        out += p.str;
        end = p.transform[4]! + p.width;
      }
      return out.replace(/ {2,}/g, ' ').trim();
    })
    .join('\n');
}

// Text jeder Seite einer PDF (gescannte Seiten: leer). Wirft bei kaputten oder geschützten PDFs. Eine einzelne Seite,
// die sich nicht lesen lässt, zählt wie eine gescannte (leer), damit nicht die ganze Datei scheitert.
export async function pdfPages(data: ArrayBuffer, onPage?: (page: number, pages: number) => void): Promise<string[]> {
  const lib = await loadPdfJs();
  const task = lib.getDocument({ data: new Uint8Array(data), ...PDF_ASSETS });
  try {
    const pdf = await task.promise;
    const out: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      try {
        const page = await pdf.getPage(i);
        out.push(pageText((await page.getTextContent()).items));
        page.cleanup();
      } catch {
        out.push('');
      }
      onPage?.(i, pdf.numPages);
    }
    return out;
  } finally {
    void task.destroy();
  }
}
